import { ServiceLocationHierarchy } from '../../../../../@types/service.type';

// ── Hierarchy helpers ────────────────────────────────────────
//
// Pure tree-walking functions only. The rendered table UI lives in
// StructureHierarchyTable.tsx, and search/"jobs only" filtering lives in
// hierarchyFilter.ts — kept separate so this file stays a stable, easily
// unit-tested set of lookups.

/** Walk the hierarchy tree to find a node by nodeTypeCode. */
export const findNodeByType = (
  node: ServiceLocationHierarchy | null,
  typeCode: string
): ServiceLocationHierarchy | null => {
  if (!node) return null;
  if (node.nodeTypeCode === typeCode) return node;
  for (const child of node.children ?? []) {
    const found = findNodeByType(child, typeCode);
    if (found) return found;
  }
  return null;
};

/** Walk to find the node that matches a given id. */
export const findNodeById = (
  node: ServiceLocationHierarchy | null,
  id: number
): ServiceLocationHierarchy | null => {
  if (!node) return null;
  if (node.id === id) return node;
  for (const child of node.children ?? []) {
    const found = findNodeById(child, id);
    if (found) return found;
  }
  return null;
};

/**
 * Walk from the root toward the node with `targetId` and return the nearest
 * ancestor outlet on that path. Anchored to the actual path to the target so it
 * stays correct when the hierarchy contains sibling branches — unlike
 * `findNodeByType`, which returns the shallowest match anywhere in the tree.
 * Returns null if the target is not in the tree, has no outlet ancestor, or
 * `targetId` is null/undefined.
 */
export const findParentOutletForNodeId = (
  node: ServiceLocationHierarchy | null,
  targetId: number | null | undefined
): ServiceLocationHierarchy | null => {
  if (!node || targetId == null) return null;
  const search = (
    current: ServiceLocationHierarchy,
    closestOutletAncestor: ServiceLocationHierarchy | null
  ): ServiceLocationHierarchy | null => {
    if (current.id === targetId) return closestOutletAncestor;
    const nextClosest = current.nodeTypeCode === 'outlet' ? current : closestOutletAncestor;
    for (const child of current.children ?? []) {
      const found = search(child, nextClosest);
      if (found) return found;
    }
    return null;
  };
  return search(node, null);
};

/**
 * Finds the node that represents the job's own location — the nearest
 * outlet on the path to `targetId` (via `findParentOutletForNodeId`), or
 * `targetId`'s own node when that node is itself an outlet.
 *
 * The Structure Hierarchy panel must anchor here, not at the top of
 * whatever `hierarchy` payload the backend returned. That payload's actual
 * top node can sit *above* the job's location (e.g. a brand, if one applies
 * to this client's structure) — starting the display there would surface
 * every other outlet under that brand as a sibling branch, and a sibling
 * location's jobs aren't relevant to this one. Anchoring on the job's own
 * outlet and only showing its descendants (its terminals) keeps the display
 * scoped to what's actually useful.
 */
export const findOwnLocationNode = (
  root: ServiceLocationHierarchy | null,
  targetId: number | null | undefined
): ServiceLocationHierarchy | null => {
  if (!root || targetId == null) return null;
  const targetNode = findNodeById(root, targetId);
  if (targetNode?.nodeTypeCode === 'outlet') return targetNode;
  return findParentOutletForNodeId(root, targetId) ?? targetNode;
};

export interface NodeTotals {
  services: number;
  parts: number;
}

/**
 * Computes service/part totals (self + every descendant) for **every** node
 * in one pass, keyed by node id. Build this once from the original,
 * unfiltered hierarchy and look totals up by id — a node's badge should
 * show its true total regardless of what a search or "jobs only" filter
 * currently prunes from view, and a node passed through the filter has its
 * `children` replaced with only the matching subset, so re-summing from
 * `node.children` directly would silently undercount once filtered.
 */
export const buildTotalCountsById = (
  node: ServiceLocationHierarchy | null
): Map<number, NodeTotals> => {
  const totalsById = new Map<number, NodeTotals>();
  if (!node) return totalsById;

  const walk = (current: ServiceLocationHierarchy): NodeTotals => {
    let services = current.serviceCount ?? 0;
    let parts = current.partCount ?? 0;
    for (const child of current.children ?? []) {
      const childTotals = walk(child);
      services += childTotals.services;
      parts += childTotals.parts;
    }
    const totals = { services, parts };
    totalsById.set(current.id, totals);
    return totals;
  };

  walk(node);
  return totalsById;
};

export interface FlatHierarchyRow {
  id: number;
  name: string;
  /** Nesting level, 0 for a top-level node — used for table-row indentation. */
  depth: number;
  services: number;
  parts: number;
  /** This node's own (pre-sum) counts, before buildTotalCountsById adds in
   *  descendants. Lets a badge tell "the record(s) behind this count are
   *  filed on this exact node" (own === total) apart from "they're actually
   *  on a child" (own < total) — see HierarchyCountBadge's `ownCount` prop. */
  ownServices: number;
  ownParts: number;
}

/**
 * Flattens a hierarchy (or filtered subset of one) into a depth-first row
 * list for the table view — one row per node, in the same order the tree
 * would render it, with its true totals (see `buildTotalCountsById`)
 * attached. Depth is preserved as a plain number so the table can indent a
 * name without needing to render anything recursively.
 */
export const flattenHierarchy = (
  nodes: ServiceLocationHierarchy[],
  totalsById: Map<number, NodeTotals>,
  depth = 0
): FlatHierarchyRow[] => {
  const rows: FlatHierarchyRow[] = [];
  for (const node of nodes) {
    const totals = totalsById.get(node.id) ?? { services: 0, parts: 0 };
    rows.push({
      id: node.id,
      name: node.name,
      depth,
      services: totals.services,
      parts: totals.parts,
      ownServices: node.serviceCount ?? 0,
      ownParts: node.partCount ?? 0,
    });
    rows.push(...flattenHierarchy(node.children ?? [], totalsById, depth + 1));
  }
  return rows;
};
